import React from 'react';
import { Factory, FlaskConical, Fuel, Building2, Briefcase, Settings, LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useIsMobile } from '@/hooks/use-mobile';

interface Industry {
  name: string;
  icon: LucideIcon;
}

const industries: Industry[] = [
  { name: 'Engineering & Equipment', icon: Settings },
  { name: 'Chemical, Food & Healthcare', icon: FlaskConical },
  { name: 'Energy and Utilities', icon: Fuel },
  { name: 'Trading Houses', icon: Building2 },
  { name: 'Professional Services', icon: Briefcase },
  { name: 'Other Allied Industries', icon: Factory }
];

// Icon Strip Design - Used for mobile devices
const IconStripDesign = () => (
  <div className="flex flex-wrap justify-center gap-6 sm:gap-10 lg:gap-12">
    {industries.map((industry, index) => (
      <div
        key={index}
        className="flex flex-col items-center gap-3 group cursor-default
                   hover:-translate-y-1 transition-all duration-200"
        role="listitem"
      >
        <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-xl bg-card border border-border/50
                        flex items-center justify-center shadow-sm
                        group-hover:shadow-md group-hover:border-primary/30
                        group-hover:bg-primary/5 transition-all duration-200">
          <industry.icon 
            className="w-7 h-7 sm:w-8 sm:h-8 text-primary
                       group-hover:scale-110 transition-transform duration-200" 
            aria-hidden="true" 
          />
        </div>
        <span className="text-xs sm:text-sm font-medium text-muted-foreground text-center
                         group-hover:text-foreground transition-colors duration-200 max-w-[100px]">
          {industry.name}
        </span>
      </div>
    ))}
  </div>
);

// Floating Cards Design - Used for tablet and desktop
const FloatingCardsDesign = () => (
  <div className="flex flex-wrap justify-center gap-4 sm:gap-5">
    {industries.map((industry, index) => (
      <div
        key={index}
        className={cn(
          "flex items-center gap-3 px-4 py-3 sm:px-5 sm:py-4",
          "bg-card rounded-xl border border-border/30",
          "shadow-md hover:shadow-lg transition-all duration-300",
          "group cursor-default",
          "hover:-translate-y-1 hover:rotate-1",
          index % 2 === 0 ? "hover:-rotate-1" : "hover:rotate-1"
        )}
        role="listitem"
      >
        <div className="w-10 h-10 sm:w-11 sm:h-11 rounded-lg bg-primary/10 
                        flex items-center justify-center
                        group-hover:bg-primary/20 transition-colors duration-200">
          <industry.icon 
            className="w-5 h-5 sm:w-6 sm:h-6 text-primary
                       group-hover:scale-110 transition-transform duration-200" 
            aria-hidden="true" 
          />
        </div>
        <span className="text-sm sm:text-base font-medium text-foreground">
          {industry.name}
        </span>
      </div>
    ))}
  </div>
);

export const IndustryShowcase: React.FC = () => {
  const isMobile = useIsMobile();

  return (
    <div role="list" aria-label="Industries we serve">
      {isMobile ? <IconStripDesign /> : <FloatingCardsDesign />}
    </div>
  );
};

export default IndustryShowcase;
